import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, ChevronDown, Search } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useAvailableBalances, useMarketAssets } from "../hooks/query/queries";
import { cn } from "../lib/utils";
import lockIcon from "../assets/icons/lock.svg";
import eightyEightFooter from "../assets/images/80eight/88-footer.svg";
import {
  formatFiatWithTwoDecimals,
  formatWithSignificantDigits,
  parseNumericValue,
} from "../utils/numberFormatter";
import {
  getSafeZar8YieldReturnPath,
  getZar8YieldAssetOption,
} from "../utils/zar8LockFlow";
import { Switch } from "./ui/switch";
import { ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND } from "./ui/zar8-yield-flow-ui";
import {
  calculateZar8YieldCalculatorProjection,
  DEFAULT_ZAR8_YIELD_CALCULATOR_ASSET_ID,
  DEFAULT_ZAR8_YIELD_CALCULATOR_DURATION_ID,
  emitZar8YieldCalculatorEvent,
  getZar8YieldCalculatorAssetLabel,
  getZar8YieldCalculatorAssetPriceInZar,
  getZar8YieldCalculatorAvailableBalanceZar,
  getZar8YieldCalculatorDurationOption,
  getZar8YieldCalculatorZar8Price,
  sanitizeZar8YieldCalculatorAmountInput,
  ZAR8_YIELD_CALCULATOR_DURATION_OPTIONS,
} from "../utils/zar8YieldCalculator";

const BTC_ICON = "/dpos/figma/icons/crypto-btc.svg";
const CALCULATOR_ASSET_IDS = ["btc", "xaut"];

function Footer() {
  return (
    <div className="border-t border-[#EBE9EF] px-4 pb-8 pt-6">
      <div className="flex items-center justify-center gap-2">
        <img src={lockIcon} alt="" aria-hidden="true" className="h-3 w-3" />
        <p className="text-sm leading-5 text-[#6D6584]">Secured by</p>
        <img
          src={eightyEightFooter}
          alt="80eight"
          className="h-4 w-auto object-contain"
        />
      </div>
    </div>
  );
}

function AssetOptionIcon({ assetId }) {
  if (assetId === "xaut") {
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(180deg,#E8D38A_0%,#C8A94B_100%)] text-lg font-semibold text-white">
        T
      </span>
    );
  }

  return (
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F7931A]">
      <img src={BTC_ICON} alt="" aria-hidden="true" className="h-5 w-5" />
    </span>
  );
}

function GhostIconButton({ children, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
    >
      {children}
    </button>
  );
}

function MaxButton({ onClick, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-10 items-center justify-center rounded-full border border-[#EBE9EF] px-5 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]",
        disabled && "cursor-not-allowed opacity-40"
      )}
    >
      Max
    </button>
  );
}

function FieldLabel({ children }) {
  return <p className="text-sm leading-5 text-[#6D6584]">{children}</p>;
}

function InputShell({ children, hasError = false, className = "" }) {
  return (
    <div
      className={cn(
        "flex min-h-[88px] items-center justify-between rounded-2xl border bg-white px-6",
        hasError ? "border-[#EB3366]" : "border-[#CECBD7]",
        className
      )}
    >
      {children}
    </div>
  );
}

function OutputField({
  label,
  value,
  secondaryValue,
  large = false,
  onSecondaryToggle,
  secondaryToggleLabel = "Switch value display",
}) {
  const SecondaryToggleElement = onSecondaryToggle ? "button" : "span";

  return (
    <div className="flex flex-col gap-3">
      <FieldLabel>{label}</FieldLabel>
      <div className="flex min-h-[88px] items-center justify-between rounded-2xl bg-[rgba(235,233,239,0.3)] px-6">
        <p
          className={cn(
            "font-semibold text-[#6D6584]",
            large ? "text-[24px] leading-8" : "text-[20px] leading-6"
          )}
        >
          {value}
        </p>
        {secondaryValue ? (
          <div className="flex items-center gap-2 text-right">
            <p className="text-base leading-6 text-[#6D6584]">
              {secondaryValue}
            </p>
            <SecondaryToggleElement
              type={onSecondaryToggle ? "button" : undefined}
              onClick={onSecondaryToggle}
              aria-label={onSecondaryToggle ? secondaryToggleLabel : undefined}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-white"
            >
              <ArrowUpDown className="h-3 w-3" />
            </SecondaryToggleElement>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div className="flex items-center justify-between py-4">
      <p className="text-base leading-6 text-[#6D6584]">{label}</p>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-[#EB3366] data-[state=unchecked]:bg-[#EBE9EF]"
      />
    </div>
  );
}

function AssetPickerModal({
  options,
  searchQuery,
  onSearchChange,
  onSelect,
  onClose,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close asset picker"
        onClick={onClose}
        className="absolute inset-0 border-0 bg-[#060606]/20 p-0 backdrop-blur-[2px]"
      />
      <div className="relative w-full max-w-[500px] rounded-[24px] border border-[#EBE9EF] bg-white px-10 pb-10 pt-6 shadow-[0_24px_80px_rgba(51,18,95,0.1)]">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold leading-6 text-[#423E51]">
            Select the asset you’d like to estimate
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-sm font-semibold leading-5 text-[#EB3366] transition-colors hover:bg-[#FFF5F8]"
          >
            Close
          </button>
        </div>

        <div className="mt-8 flex h-14 items-center gap-3 rounded-full border border-[#EBE9EF] px-6">
          <Search className="h-3 w-3 text-[#6D6584]" />
          <input
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search"
            aria-label="Search assets"
            className="w-full bg-transparent text-sm leading-5 text-[#423E51] outline-none placeholder:text-[#6D6584]"
          />
        </div>

        <div className="mt-[18px] space-y-[9px]">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onSelect(option.id)}
              className="flex min-h-[88px] w-full items-center rounded-2xl border border-[#EBE9EF] px-4 text-left transition-colors hover:bg-[#FAF9FC]"
            >
              <AssetOptionIcon assetId={option.id} />
              <div className="ml-3 min-w-0">
                <p className="truncate text-[20px] font-semibold leading-6 text-[#423E51]">
                  {getZar8YieldCalculatorAssetLabel(option.id)}
                </p>
                <p className="mt-2 text-sm leading-5 text-[#6D6584]">
                  {option.subtitle}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function DurationPicker({ selectedDurationId, onSelect, onClose }) {
  return (
    <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 rounded-2xl border border-[#EBE9EF] bg-white py-2 shadow-[0_24px_80px_rgba(51,18,95,0.08)]">
      {ZAR8_YIELD_CALCULATOR_DURATION_OPTIONS.map((option) => {
        const isSelected = option.id === selectedDurationId;

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              onSelect(option.id);
              onClose();
            }}
            className="flex w-full items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-[#FAF9FC]"
          >
            <span
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border",
                isSelected
                  ? "border-[2px] border-[#FF315F]"
                  : "border-[#CECBD7] p-[2px]"
              )}
            >
              {isSelected ? (
                <span className="h-5 w-5 rounded-full bg-[#FF315F]" />
              ) : null}
            </span>
            <span
              className={cn(
                "text-base font-semibold leading-6",
                isSelected ? "text-[#423E51]" : "text-[#6D6584]"
              )}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function Zar8YieldCalculatorContent({
  basePath = "/zar8/track",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const userId = userProfile?._id;
  const returnTo = getSafeZar8YieldReturnPath(
    location.state?.returnTo,
    basePath
  );
  const [amountInput, setAmountInput] = useState("");
  const [amountMode, setAmountMode] = useState("zar");
  const [selectedAssetId, setSelectedAssetId] = useState(
    DEFAULT_ZAR8_YIELD_CALCULATOR_ASSET_ID
  );
  const [selectedDurationId, setSelectedDurationId] = useState(
    DEFAULT_ZAR8_YIELD_CALCULATOR_DURATION_ID
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isAssetPickerOpen, setIsAssetPickerOpen] = useState(false);
  const [isDurationPickerOpen, setIsDurationPickerOpen] = useState(false);
  const [useLivePrice, setUseLivePrice] = useState(true);
  const [lockedAssetPrice, setLockedAssetPrice] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [amountTouched, setAmountTouched] = useState(false);
  const [rewardDisplayMode, setRewardDisplayMode] = useState("asset");
  const { data: availableBalances } = useAvailableBalances(userId);
  const marketAssetsQuery = useMarketAssets();
  const marketAssets = marketAssetsQuery.data;

  const zar8Asset = useMemo(
    () =>
      availableBalances?.find(
        (asset) => asset?.asset?.toLowerCase() === "zar8"
      ) || null,
    [availableBalances]
  );
  const selectedAsset = useMemo(
    () => getZar8YieldAssetOption(selectedAssetId),
    [selectedAssetId]
  );
  const selectedDuration = useMemo(
    () => getZar8YieldCalculatorDurationOption(selectedDurationId),
    [selectedDurationId]
  );
  const filteredAssets = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const allAssetOptions = CALCULATOR_ASSET_IDS.map((assetId) =>
      getZar8YieldAssetOption(assetId)
    ).filter(Boolean);

    if (!normalizedQuery) {
      return allAssetOptions;
    }

    return allAssetOptions.filter((option) =>
      `${option?.label} ${option?.subtitle}`
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [searchQuery]);
  const availableBalanceZar =
    getZar8YieldCalculatorAvailableBalanceZar(zar8Asset);
  const availableBalanceZar8 = parseNumericValue(zar8Asset?.availableAmount);
  const zar8PriceInZar = getZar8YieldCalculatorZar8Price(zar8Asset);
  const liveAssetPriceInZar = getZar8YieldCalculatorAssetPriceInZar(
    marketAssets,
    selectedAssetId
  );
  const assetPriceInZar = useLivePrice
    ? liveAssetPriceInZar
    : (lockedAssetPrice ?? liveAssetPriceInZar);
  const projection = useMemo(
    () =>
      calculateZar8YieldCalculatorProjection({
        amountInput,
        amountMode,
        assetId: selectedAssetId,
        durationId: selectedDurationId,
        assetPriceInZar,
        zar8PriceInZar,
      }),
    [
      amountInput,
      amountMode,
      assetPriceInZar,
      selectedAssetId,
      selectedDurationId,
      zar8PriceInZar,
    ]
  );
  const hasValidAmount = projection.principalZar > 0;
  const amountError =
    amountTouched && !hasValidAmount
      ? "Enter a valid amount greater than zero."
      : "";
  const isCalculateDisabled =
    !hasValidAmount || !selectedAsset || !selectedDuration;
  const title = `${getZar8YieldCalculatorAssetLabel(selectedAssetId)} Calculator`;
  const priceLabel = `${getZar8YieldCalculatorAssetLabel(
    selectedAssetId
  )} price (ZAR)`;
  const yieldLabel = `Estimated ${getZar8YieldCalculatorAssetLabel(
    selectedAssetId
  )} earned`;
  const toggleLabel = `Use live ${getZar8YieldCalculatorAssetLabel(
    selectedAssetId
  )} price`;
  const amountFieldLabel =
    amountMode === "zar"
      ? "Investment amount (ZAR)"
      : "Investment amount (ZAR8)";
  const amountConversionLabel = hasValidAmount
    ? amountMode === "zar"
      ? `≈ ${formatWithSignificantDigits(projection.principalZar8, {
          minSignificant: 4,
          maxDecimals: 6,
        })} ZAR8`
      : `≈ ${formatFiatWithTwoDecimals(projection.principalZar)}`
    : "";
  const rewardsSecondaryValue =
    calculation && rewardDisplayMode === "asset"
      ? `≈ ${formatFiatWithTwoDecimals(calculation.estimatedRewardsZar)}`
      : calculation
        ? `≈ ${formatWithSignificantDigits(calculation.estimatedAssetEarned, {
            minSignificant: 4,
            maxDecimals: selectedAssetId === "btc" ? 8 : 6,
          })} ${getZar8YieldCalculatorAssetLabel(selectedAssetId)}`
        : "";
  const rewardsPrimaryValue =
    calculation && rewardDisplayMode === "asset"
      ? `${formatWithSignificantDigits(calculation.estimatedAssetEarned, {
          minSignificant: 4,
          maxDecimals: selectedAssetId === "btc" ? 8 : 6,
        })} ${getZar8YieldCalculatorAssetLabel(selectedAssetId)}`
      : calculation
        ? formatFiatWithTwoDecimals(calculation.estimatedRewardsZar)
        : "----";
  const rewardsOutputLabel =
    rewardDisplayMode === "asset" ? yieldLabel : "Estimated reward value";
  const livePriceUnavailable = selectedAsset && liveAssetPriceInZar <= 0;

  useEffect(() => {
    emitZar8YieldCalculatorEvent("zar8_yield_calculator_opened", {
      returnTo,
    });
  }, [returnTo]);

  useEffect(() => {
    setCalculation(null);
  }, [
    amountInput,
    amountMode,
    selectedAssetId,
    selectedDurationId,
    useLivePrice,
  ]);

  useEffect(() => {
    setRewardDisplayMode("asset");
  }, [selectedAssetId]);

  useEffect(() => {
    if (!useLivePrice && liveAssetPriceInZar > 0 && lockedAssetPrice == null) {
      setLockedAssetPrice(liveAssetPriceInZar);
    }
  }, [liveAssetPriceInZar, lockedAssetPrice, useLivePrice]);

  const handleAmountChange = (nextValue) => {
    setAmountTouched(true);
    setAmountInput(
      sanitizeZar8YieldCalculatorAmountInput(nextValue, amountMode)
    );
  };

  const handleAmountModeToggle = () => {
    setAmountTouched(false);
    setAmountInput("");
    setAmountMode((currentMode) => (currentMode === "zar" ? "zar8" : "zar"));
  };

  const handleUseMax = () => {
    setAmountTouched(true);
    setAmountInput(
      amountMode === "zar"
        ? `${availableBalanceZar}`
        : `${availableBalanceZar8}`
    );
  };

  const handleCalculate = () => {
    if (isCalculateDisabled) {
      setAmountTouched(true);
      return;
    }

    if (!useLivePrice && liveAssetPriceInZar > 0) {
      setLockedAssetPrice(liveAssetPriceInZar);
    }

    setCalculation(projection);
    emitZar8YieldCalculatorEvent("zar8_yield_calculation_triggered", {
      amountMode,
      assetId: selectedAssetId,
      durationId: selectedDurationId,
      principalZar: projection.principalZar,
      useLivePrice,
    });
  };

  const handleToggleLivePrice = (nextValue) => {
    setUseLivePrice(nextValue);

    if (nextValue) {
      setLockedAssetPrice(null);
      return;
    }

    if (liveAssetPriceInZar > 0) {
      setLockedAssetPrice(liveAssetPriceInZar);
    }
  };

  const handleToggleRewardDisplay = () => {
    setRewardDisplayMode((currentMode) =>
      currentMode === "asset" ? "zar" : "asset"
    );
  };

  return (
    <main
      className="min-h-[calc(100vh-96px)] px-5 pb-12 pt-7 md:px-8 xl:px-[52px] xl:pt-9"
      style={{
        background:
          "linear-gradient(180deg, rgba(248, 248, 248, 1) 0%, rgba(255, 255, 255, 1) 100%)",
      }}
    >
      {isAssetPickerOpen ? (
        <AssetPickerModal
          options={filteredAssets}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={(assetId) => {
            setSelectedAssetId(assetId);
            setIsAssetPickerOpen(false);
            setSearchQuery("");
          }}
          onClose={() => {
            setIsAssetPickerOpen(false);
            setSearchQuery("");
          }}
        />
      ) : null}

      <div className="mx-auto max-w-[739px] overflow-visible rounded-[24px] border border-[#EBE9EF] bg-white pt-10">
        <div className="relative px-6 pb-10 sm:px-[33px]">
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            className="absolute right-6 top-[-8px] rounded-full px-4 py-3 text-sm font-semibold leading-5 text-[#EB3366] transition-colors hover:bg-[#FFF5F8]"
          >
            Cancel
          </button>

          <h1 className="text-center text-xl font-semibold leading-7 text-[#57249C]">
            {title}
          </h1>

          <div className="mt-10 flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <FieldLabel>{amountFieldLabel}</FieldLabel>
              <InputShell hasError={Boolean(amountError)}>
                <div className="flex min-w-0 flex-1 items-center gap-4">
                  <input
                    value={amountInput}
                    onChange={(event) => handleAmountChange(event.target.value)}
                    onBlur={() => setAmountTouched(true)}
                    inputMode="decimal"
                    aria-label={amountFieldLabel}
                    className="w-full bg-transparent text-[24px] font-semibold leading-8 text-[#33125F] outline-none placeholder:text-[#B8B2C7]"
                  />
                </div>
                <div className="ml-4 flex items-center gap-2">
                  {amountConversionLabel ? (
                    <p className="hidden text-base leading-6 text-[#6D6584] sm:block">
                      {amountConversionLabel}
                    </p>
                  ) : null}
                  <GhostIconButton
                    onClick={handleAmountModeToggle}
                    label="Swap amount mode"
                  >
                    <ArrowUpDown className="h-3 w-3" />
                  </GhostIconButton>
                  <MaxButton
                    onClick={handleUseMax}
                    disabled={
                      amountMode === "zar"
                        ? availableBalanceZar <= 0
                        : availableBalanceZar8 <= 0
                    }
                  />
                </div>
              </InputShell>
              {amountError ? (
                <p className="text-sm leading-5 text-[#EB3366]">
                  {amountError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-3">
              <FieldLabel>Yield Asset</FieldLabel>
              <InputShell>
                <button
                  type="button"
                  onClick={() => setIsAssetPickerOpen(true)}
                  className="flex w-full items-center justify-between gap-4 text-left"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <AssetOptionIcon assetId={selectedAssetId} />
                    <span className="min-w-0">
                      <span className="block text-[20px] font-semibold leading-6 text-[#423E51]">
                        {getZar8YieldCalculatorAssetLabel(selectedAssetId)}
                      </span>
                      <span className="mt-2 block text-sm leading-5 text-[#6D6584]">
                        {selectedAsset?.subtitle || "Bitcoin"}
                      </span>
                    </span>
                  </span>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584]">
                    <ChevronDown className="h-3 w-3" />
                  </span>
                </button>
              </InputShell>
            </div>

            <div className="relative flex flex-col gap-3">
              <FieldLabel>Duration</FieldLabel>
              <InputShell>
                <button
                  type="button"
                  onClick={() => setIsDurationPickerOpen((current) => !current)}
                  className="flex w-full items-center justify-between gap-4 text-left"
                >
                  <span className="text-[24px] font-semibold leading-8 text-[#33125F]">
                    {selectedDuration?.label || "Select duration"}
                  </span>
                  <span className="text-[#6D6584]">
                    <ChevronDown className="h-3 w-3" />
                  </span>
                </button>
              </InputShell>
              {isDurationPickerOpen ? (
                <>
                  <button
                    type="button"
                    aria-label="Close duration picker"
                    className="fixed inset-0 z-10 border-0 bg-transparent p-0"
                    onClick={() => setIsDurationPickerOpen(false)}
                  />
                  <DurationPicker
                    selectedDurationId={selectedDurationId}
                    onSelect={setSelectedDurationId}
                    onClose={() => setIsDurationPickerOpen(false)}
                  />
                </>
              ) : null}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <OutputField
                label="Net annual yield (%)"
                value={
                  calculation
                    ? `${formatWithSignificantDigits(
                        calculation.annualYieldRate * 100,
                        {
                          minSignificant: 3,
                          maxDecimals: 2,
                        }
                      )}%`
                    : "----"
                }
              />
              <OutputField
                label={priceLabel}
                value={
                  calculation && assetPriceInZar > 0
                    ? formatFiatWithTwoDecimals(assetPriceInZar)
                    : "----"
                }
              />
            </div>

            <Toggle
              checked={useLivePrice}
              onChange={handleToggleLivePrice}
              label={toggleLabel}
            />

            {livePriceUnavailable ? (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-[#EBE9EF] px-4 py-3">
                <p className="text-sm leading-5 text-[#6D6584]">
                  We couldn’t load the live{" "}
                  {getZar8YieldCalculatorAssetLabel(selectedAssetId)} price
                  right now. ZAR rewards can still be estimated.
                </p>
                <button
                  type="button"
                  onClick={() => marketAssetsQuery.refetch?.()}
                  className="rounded-full border border-[#EBE9EF] px-4 py-2 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
                >
                  Retry
                </button>
              </div>
            ) : null}

            <OutputField
              label="Estimated ZAR rewards (in rand)"
              value={
                calculation
                  ? formatFiatWithTwoDecimals(calculation.estimatedRewardsZar)
                  : "----"
              }
            />

            <OutputField
              label={rewardsOutputLabel}
              large
              value={rewardsPrimaryValue}
              secondaryValue={calculation ? rewardsSecondaryValue : ""}
              onSecondaryToggle={handleToggleRewardDisplay}
              secondaryToggleLabel="Switch estimated reward display"
            />

            <div className="pt-4">
              <p className="text-sm leading-5 text-[#6D6584]">
                Estimates use the selected duration, current yield assumptions,
                and the {getZar8YieldCalculatorAssetLabel(selectedAssetId)}{" "}
                price at calculation time. Actual returns can vary.
              </p>
            </div>
          </div>

          <div className="mt-10 flex items-center justify-center gap-4">
            <GhostIconButton
              onClick={() => navigate(returnTo)}
              label="Back to overview"
            >
              <ArrowLeft className="h-4 w-4" />
            </GhostIconButton>
            <button
              type="button"
              disabled={isCalculateDisabled}
              onClick={handleCalculate}
              className={cn(
                "h-12 w-full max-w-[302px] rounded-full px-6 text-sm font-semibold leading-5 transition-colors",
                isCalculateDisabled
                  ? "bg-[#F2F1F4] text-[#9D96AF]"
                  : "text-white"
              )}
              style={
                isCalculateDisabled
                  ? undefined
                  : { background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND }
              }
            >
              Calculate
            </button>
          </div>
        </div>

        <Footer />
      </div>
    </main>
  );
}
